import { useCallback, useMemo } from 'react';
import { ChargerMarkerProps } from '../components/leaflet-map/leaflet-map.types';
import { ILocation } from '../api/locations/types';
import { EStationPricingOption } from '../api/types';
import { EStationStatus, IStation, IStationConnector } from '../api/station/types';
import { useIntl } from 'react-intl';
import { useLocations } from './useLocations';
import { locationsApi } from '../api/locations/locations.api';
import { useSelector } from 'react-redux';
import { getStationCurrency } from '../utils/station';

export const useLocationsData = (locationId?: string) => {
    const {
        locations,
        isLoading: isLocationsLoading,
        isFetching: isLocationsReloading,
    } = useLocations();

    const locationSelector = useCallback(
        (state: any) =>
            locationId
                ? locationsApi.endpoints.getLocationById.select(locationId)(state)
                : undefined,
        [locationId]
    );

    const locationData = useSelector(locationSelector);

    const intl = useIntl();

    const mappedLocations = useMemo(() => {
        return (locations?.data || [])?.reduce(
            (acc: ChargerMarkerProps[], _location: ILocation) => {
                const location =
                    locationData?.data?.data?._id === _location._id
                        ? locationData?.data?.data
                        : _location;

                const connectors = (location.stations || [])?.reduce(
                    (accumulator: (IStationConnector & { station: IStation })[], station) => {
                        const mappedConnectors = station?.connectors?.map(connector => {
                            return {
                                ...connector,
                                station,
                            };
                        });
                        return [...accumulator, ...mappedConnectors];
                    },
                    []
                );

                const availableConnectors = connectors?.filter(
                    connector =>
                        connector.status === EStationStatus.AVAILABLE &&
                        connector.station.online &&
                        connector.station.mode !== 'Inactive'
                );

                const busyConnectors = connectors?.filter(
                    connector =>
                        [
                            EStationStatus.PREPARING,
                            EStationStatus.CHARGING,
                            EStationStatus.SUSPENDED_EV,
                            EStationStatus.SUSPENDED_EVSE,
                            EStationStatus.FINISHING,
                            EStationStatus.RESERVED,
                        ].includes(connector.status) &&
                        connector.station.online &&
                        connector.station.mode !== 'Inactive'
                )?.length;

                const connectorsForPricing =
                    availableConnectors.length > 0 ? availableConnectors : connectors;

                const pricingOptions = connectorsForPricing.map(connector => {
                    let price;
                    if (connector.tariff?.pricingOption === EStationPricingOption.hour) {
                        price = connector.tariff?.priceForHour;
                    }

                    if (connector.tariff?.pricingOption === EStationPricingOption.kwh) {
                        price =
                            connector.tariff?.priceForKwh ||
                            // @ts-expect-error BE returns diff prop name
                            connector.tariff?.priceForkwh;
                    }

                    return {
                        pricingOption: connector.tariff?.pricingOption,
                        price,
                        currency: getStationCurrency(connector.station),
                    };
                });

                const isFree =
                    (connectors ?? [])?.some(connector => {
                        return connector.tariff?.pricingOption === EStationPricingOption.free;
                    }) ?? false;

                const kwhPricing = pricingOptions.filter(
                    option => option.pricingOption === EStationPricingOption.kwh && option.price
                );
                const hourPricing = pricingOptions.filter(
                    option => option.pricingOption === EStationPricingOption.hour && option.price
                );

                let price = '';
                let pricingOptionLabel = '';

                if (kwhPricing.length > 0) {
                    const minKwhPriceOption = kwhPricing.reduce(
                        (min, current) =>
                            Number(current.price) < Number(min.price) ? current : min,
                        kwhPricing[0]
                    );
                    price = `${Number((minKwhPriceOption.price || 0) / 100).toFixed(2)} ${minKwhPriceOption.currency || ''}`;
                    pricingOptionLabel = intl.formatMessage({ id: 'unit.perkWh' });
                } else if (hourPricing.length > 0) {
                    const minHourPriceOption = hourPricing.reduce(
                        (min, current) =>
                            Number(current.price) < Number(min.price) ? current : min,
                        hourPricing[0]
                    );
                    price = `${Number((minHourPriceOption.price || 0) / 100).toFixed(2)} ${minHourPriceOption.currency || ''}`;
                    pricingOptionLabel = intl.formatMessage({ id: 'unit.perHour' });
                }

                const totalConnectors = connectors.length;
                const availableConnectorsCount = availableConnectors.length;

                const mappedLocation = {
                    id: location._id,
                    location: location?.location?.coordinates.toReversed() as [number, number],
                    totalConnectors,
                    availableConnectors: availableConnectorsCount,
                    unavailableConnectors:
                        totalConnectors - availableConnectorsCount - busyConnectors,
                    busyConnectors,
                    isFree,
                    price,
                    pricingOptionLabel,
                };

                acc.push(mappedLocation);

                return acc;
            },
            []
        );
    }, [locations, intl, locationData]);

    return { mappedLocations, isLocationsLoading, isLocationsReloading };
};
